import React, { useEffect, useRef, useState } from 'react';
import { Minus, Move, Plus, RotateCcw } from 'lucide-react';
interface BeachCameraViewerProps {
  isReady: boolean;
}
interface Position {
  x: number;
  y: number;
}
interface DragStart extends Position {
  pointerX: number;
  pointerY: number;
}
const MIN_ZOOM = 1;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.25;
const VIDEO_ID = 'vWW1tVNxCAA';
export function BeachCameraViewer({
  isReady
}: BeachCameraViewerProps) {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [position, setPosition] = useState<Position>({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<DragStart | null>(null);
  useEffect(() => {
    if (zoom === MIN_ZOOM) {
      setPosition({
        x: 0,
        y: 0
      });
    }
  }, [zoom]);
  const getBounds = (nextZoom = zoom) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return {
      maxX: 0,
      maxY: 0
    };
    return {
      maxX: rect.width * (nextZoom - 1) / 2,
      maxY: rect.height * (nextZoom - 1) / 2
    };
  };
  const updateZoom = (direction: 1 | -1) => {
    setZoom(currentZoom => {
      const nextZoom = clamp(currentZoom + direction * ZOOM_STEP, MIN_ZOOM, MAX_ZOOM);
      const {
        maxX,
        maxY
      } = getBounds(nextZoom);
      setPosition(currentPosition => ({
        x: clamp(currentPosition.x, -maxX, maxX),
        y: clamp(currentPosition.y, -maxY, maxY)
      }));
      return nextZoom;
    });
  };
  const resetView = () => {
    setZoom(MIN_ZOOM);
    setPosition({
      x: 0,
      y: 0
    });
  };
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (zoom === MIN_ZOOM) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: position.x,
      y: position.y
    };
    setIsDragging(true);
  };
  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const dragStart = dragStartRef.current;
    if (!isDragging || !dragStart) return;
    const {
      maxX,
      maxY
    } = getBounds();
    setPosition({
      x: clamp(dragStart.x + event.clientX - dragStart.pointerX, -maxX, maxX),
      y: clamp(dragStart.y + event.clientY - dragStart.pointerY, -maxY, maxY)
    });
  };
  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStartRef.current = null;
    setIsDragging(false);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (zoom === MIN_ZOOM) return;
    const movementByKey: Record<string, Position> = {
      ArrowLeft: {
        x: 24,
        y: 0
      },
      ArrowRight: {
        x: -24,
        y: 0
      },
      ArrowUp: {
        x: 0,
        y: 24
      },
      ArrowDown: {
        x: 0,
        y: -24
      }
    };
    const movement = movementByKey[event.key];
    if (!movement) return;
    event.preventDefault();
    const {
      maxX,
      maxY
    } = getBounds();
    setPosition(currentPosition => ({
      x: clamp(currentPosition.x + movement.x, -maxX, maxX),
      y: clamp(currentPosition.y + movement.y, -maxY, maxY)
    }));
  };
  const isZoomed = zoom > MIN_ZOOM;
  const playerUrl = `https://www.youtube-nocookie.com/embed/${VIDEO_ID}?controls=1&playsinline=1&rel=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}&widget_referrer=${encodeURIComponent(window.location.origin)}`;
  return <div className="max-w-4xl mx-auto mb-8" data-id="element-69" data-mp="1yojn0co14fri">
      <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-end sm:justify-between" data-id="element-70" data-mp="1ypuq0a9nhjhk">
        <div className="text-right" data-id="element-71" data-mp="23n38yd2mhrjv">
          <h3 className="text-2xl font-bold text-[#0A5F8F]" data-id="element-72" data-mp="3t212rdi708bz">
            מצלמת החוף בשידור חי
          </h3>
          <p className="text-gray-600 text-sm mt-1" data-id="element-73" data-mp="1vzwrjd344x7">
            צפו בתנאים בחוף מעגן בזמן אמת
          </p>
        </div>

        <div className="flex items-center justify-end gap-2" role="group" aria-label="פקדי זום למצלמת החוף" dir="ltr" data-id="element-74" data-mp="1xxjqj0il2m55">
          <button type="button" onClick={() => updateZoom(-1)} disabled={zoom === MIN_ZOOM} className="w-10 h-10 rounded-lg border border-gray-300 bg-white text-[#0A5F8F] hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center" aria-label="הקטנת התמונה" data-id="element-75" data-mp="1oxog4xwuqv66">
            <Minus className="w-5 h-5" data-id="element-76" data-mp="159xh3bmocnad" />
          </button>
          <span className="min-w-14 text-center text-sm font-bold text-gray-700" aria-live="polite" data-id="element-77" data-mp="15agviid48tjn">
            {Math.round(zoom * 100)}%
          </span>
          <button type="button" onClick={() => updateZoom(1)} disabled={zoom === MAX_ZOOM} className="w-10 h-10 rounded-lg border border-gray-300 bg-white text-[#0A5F8F] hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center" aria-label="הגדלת התמונה" data-id="element-78" data-mp="w7xzoi7qzh6w">
            <Plus className="w-5 h-5" data-id="element-79" data-mp="3cwqujcz64vod" />
          </button>
          <button type="button" onClick={resetView} disabled={!isZoomed} className="h-10 px-3 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5 whitespace-nowrap" aria-label="איפוס התצוגה" data-id="element-80" data-mp="3gxane4cpld4p">
            <RotateCcw className="w-4 h-4" data-id="element-81" data-mp="2t5vic3tjshpr" />
            איפוס
          </button>
        </div>
      </div>

      <div ref={viewportRef} dir="ltr" tabIndex={isZoomed ? 0 : -1} onKeyDown={handleKeyDown} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={stopDragging} onPointerCancel={stopDragging} className={`relative aspect-video overflow-hidden rounded-xl md:rounded-3xl bg-black shadow-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#0A5F8F] focus:ring-offset-2 ${isZoomed ? isDragging ? 'cursor-grabbing touch-none' : 'cursor-grab touch-none' : ''}`} aria-label={isZoomed ? 'תצוגת מצלמה מוגדלת. ניתן לגרור או להשתמש במקשי החיצים להזזה.' : undefined} data-id="element-82" data-mp="2y09kqq6mnp3x">
        {isReady ? <iframe src={playerUrl} title="מצלמת חוף מעגן בשידור חי" className={`absolute inset-0 w-full h-full border-0 origin-center ${isZoomed ? 'pointer-events-none' : ''} ${isDragging ? '' : 'transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none'}`} style={{
        transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${zoom})`
      }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen data-id="element-83" data-mp="2kwdwqygyue4b" /> : <div className="w-full h-full flex items-center justify-center text-white text-sm" data-id="element-84" data-mp="7pxjhsij1mq4">
            טוען את מצלמת החוף…
          </div>}

        {isZoomed && <div className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-black/75 px-3 py-1.5 text-xs font-medium text-white whitespace-nowrap" data-id="element-85" data-mp="10e35sjp8bjs2">
            <Move className="w-4 h-4" data-id="element-86" data-mp="qmgw4rsjhsrf" />
            גררו את התמונה לשינוי אזור הצפייה
          </div>}
      </div>

      <p className="text-gray-500 text-xs text-center mt-2" data-id="element-87" data-mp="vkih5i9j025j">
        הגדילו את התמונה ואז גררו אותה עם העכבר או האצבע. לחזרה לבקרי YouTube לחצו איפוס.
      </p>
    </div>;
}
function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}